import { IconNames } from '@blueprintjs/icons';
import { ActionButtonText } from '@strata/react-buttons';
import { InlineMessage } from '@strata/react-inline-message';
import { TextInputFormik } from '@strata/react-inputs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuggestMultiSelectFormik, SuggestSingleSelectFormik } from '@strata/react-selects';
import { FieldArray, Form, useFormikContext } from 'formik';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { v4 as uuidv4 } from 'uuid';

import { ServiceComponent } from 'components/wonReferrals/claimSubmission/ServiceComponent';
import { YesNoRadioOptions } from 'services/constants';
import {
    ClaimPlaceOfServiceOptions,
    ClaimPlaceOfServiceReferralPlaceOfServiceMap,
    type ClaimSubmissionFormSchema,
    ProviderAcceptAssignmentOptions,
    ReleaseOfInformationOptions,
} from 'services/MedicalBillingConstants';
import { AppRoutes } from 'services/routes';
import { ConversationContexts, MESSAGES_LOCATION_HASH } from 'services/userSupportHelpers';
import {
    type DiagnosisCodeOption,
    type ProductCodeOption,
    type SupplierDetails,
} from 'store/api/referenceApi';
import { type ReferralDetails } from 'store/api/treatmentApi';

interface ClaimInformationSectionProps {
    isRevision?: boolean;
    referralDetails?: ReferralDetails;
    parentSupplierDetails?: SupplierDetails;
    childSupplierDetails?: SupplierDetails;
    diagnosesList?: DiagnosisCodeOption[];
    productCodeDropdownOptions: ProductCodeOption[];
}

export const ClaimInformationSection = ({
    isRevision = false,
    referralDetails = undefined,
    parentSupplierDetails = undefined,
    childSupplierDetails = undefined,
    diagnosesList = undefined,
    productCodeDropdownOptions,
}: ClaimInformationSectionProps) => {
    const navigate = useNavigate();
    const formik = useFormikContext<ClaimSubmissionFormSchema>();

    // Only include primary & additional diagnoses as possible options for service diagnoses
    const { primaryDiagnosis, additionalDiagnoses } = formik.values ?? {};
    const serviceDiagnosesValues = useMemo(
        () => [...(primaryDiagnosis ? [primaryDiagnosis] : []), ...additionalDiagnoses],
        [primaryDiagnosis, additionalDiagnoses],
    );

    const beginPlaceOfServiceSupportConversation = () =>
        navigate(
            {
                pathname: AppRoutes.wonReferralDetails(referralDetails?.referralId),
                hash: MESSAGES_LOCATION_HASH,
            },
            { state: { beginConversationContext: ConversationContexts.CLAIM_REJECTION } },
        );

    return (
        <Form autoComplete="off">
            <div className="margin__bottom--large" style={{ width: '45%' }}>
                {isRevision && (
                    <TextInputFormik
                        id="originalClaimNumber"
                        label="Original Payer Claim ID"
                        required
                    />
                )}
                <SuggestSingleSelectFormik
                    id="supplierNpi"
                    label="Supplier NPI"
                    required
                    values={parentSupplierDetails?.childNpis}
                    isLoading={!parentSupplierDetails?.childNpis}
                    onChange={(newValue) => {
                        void (async () => {
                            await formik.setFieldValue('supplierNpi', newValue);
                            await formik.setFieldValue('taxonomyCode', undefined);
                        })();
                    }}
                />
                <SuggestSingleSelectFormik
                    id="taxonomyCode"
                    label="Taxonomy Code"
                    required
                    values={childSupplierDetails?.taxonomyCodes}
                    isLoading={!childSupplierDetails?.taxonomyCodes}
                    selectedItem={childSupplierDetails?.taxonomyCodes?.find(
                        ({ name }) => name === formik.values.taxonomyCode,
                    )}
                    onChange={(newVal) => {
                        void formik.setFieldValue('taxonomyCode', newVal?.name);
                    }}
                />
                <SuggestSingleSelectFormik
                    id="placeOfService"
                    label="Place Of Service"
                    required
                    values={ClaimPlaceOfServiceOptions}
                />
                {formik.values.placeOfService?.secondaryData &&
                    ClaimPlaceOfServiceReferralPlaceOfServiceMap[
                        formik.values.placeOfService.secondaryData
                    ] !== referralDetails?.placeOfService && (
                        <div className="margin__bottom--medium">
                            <InlineMessage
                                wrapText
                                intent={InlineMessage.Intent.WARNING}
                                label={
                                    <>
                                        The selected place of service does not match this referral.
                                        The place of service determines the rate at which the drug
                                        will be billed. You may begin a{' '}
                                        <u
                                            role="button"
                                            style={{ cursor: 'pointer' }}
                                            tabIndex={0}
                                            onClick={beginPlaceOfServiceSupportConversation}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    beginPlaceOfServiceSupportConversation();
                                                }
                                            }}
                                        >
                                            Support Conversation
                                        </u>{' '}
                                        if further assistance is needed.
                                    </>
                                }
                            />
                        </div>
                    )}
                <TextInputFormik
                    id="memberId"
                    label="Member ID"
                    required
                    helperText="Enter the member ID including the alpha prefix"
                />
                <TextInputFormik
                    id="patientAccountNumber"
                    label="Patient Account Number"
                    required
                />
                <RadioButtonGroupFormik
                    name="providerSignatureOnFile"
                    label="Provider Signature On File"
                    required
                    options={YesNoRadioOptions}
                />
                <SuggestSingleSelectFormik
                    id="providerAcceptAssignment"
                    label="Provider Accept Assignment"
                    required
                    values={ProviderAcceptAssignmentOptions}
                />
                <RadioButtonGroupFormik
                    name="assignmentOfBenefits"
                    label="Assignment Of Benefits"
                    required
                    options={YesNoRadioOptions}
                />
                <SuggestSingleSelectFormik
                    id="releaseOfInformation"
                    label="Release Of Information"
                    required
                    values={ReleaseOfInformationOptions}
                />
                <SuggestSingleSelectFormik
                    id="primaryDiagnosis"
                    label="Primary Diagnosis"
                    required
                    values={diagnosesList}
                    onChange={(newValue) => {
                        void (() =>
                            // When primary diagnosis is changed, update the diagnoses value in service 0 to the new value & remove old value from all additional services
                            formik.setValues({
                                ...formik.values,
                                primaryDiagnosis: newValue,
                                additionalDiagnoses: formik.values.additionalDiagnoses.filter(
                                    (diagnosis) => diagnosis !== newValue,
                                ),
                                services: formik.values.services.map((service, index) => ({
                                    ...service,
                                    diagnoses: [
                                        ...(index === 0 ? [newValue] : []),
                                        ...service.diagnoses.filter(
                                            (diagnosis) =>
                                                !(
                                                    diagnosis === formik.values.primaryDiagnosis ||
                                                    (index === 0 && diagnosis === newValue)
                                                ),
                                        ),
                                    ],
                                })),
                            } as ClaimSubmissionFormSchema))();
                    }}
                />
                <SuggestMultiSelectFormik
                    id="additionalDiagnoses"
                    label="Additional Diagnoses"
                    helperText="11 additional diagnoses max. This list will populate service diagnoses below."
                    values={
                        diagnosesList?.filter(
                            (diagnosis) => diagnosis !== formik.values.primaryDiagnosis,
                        ) ?? []
                    }
                    onChange={(newValues) => {
                        void (() =>
                            // When an additional diagnosis is removed, also remove it from all service diagnoses
                            formik.setValues({
                                ...formik.values,
                                additionalDiagnoses: newValues,
                                services: formik.values.services.map((service) => ({
                                    ...service,
                                    diagnoses: service.diagnoses.filter(
                                        (diagnosis) =>
                                            diagnosis === formik.values.primaryDiagnosis ||
                                            newValues.includes(diagnosis),
                                    ),
                                })),
                            }))();
                    }}
                />
            </div>
            <FieldArray name="services">
                {(arrayHelpers) => (
                    <div className="margin__bottom--small">
                        {formik.values[arrayHelpers.name as 'services'].map((service, index) => (
                            <ServiceComponent
                                key={service.key}
                                index={index}
                                service={service}
                                referralDetails={referralDetails}
                                productCodeDropdownOptions={productCodeDropdownOptions}
                                serviceDiagnosesValues={serviceDiagnosesValues}
                                arrayHelpers={arrayHelpers}
                            />
                        ))}
                        <ActionButtonText
                            label="Add Additional Service"
                            id="add-additional-service-button"
                            onClick={() => arrayHelpers.push({ key: uuidv4(), diagnoses: [] })}
                            leftIcon={IconNames.ADD}
                        />
                    </div>
                )}
            </FieldArray>
        </Form>
    );
};
